import { ClockIcon } from '@phosphor-icons/react';
import { AirlineLogo } from '../../shared/airline_logo/airline_logo.js';
import './flight_timeline.css';

/** Logo da cia no cabeçalho do drawer "Detalhe do voo": 40px (handoff). Maior que
 *  a marca inline padronizada (`AIRLINE_MARK_SIZE`=22) das offer rows / cards — o
 *  cabeçalho do drawer é o único contexto em destaque que usa 40px. */
const DRAWER_HEADER_LOGO = 40;

/** Nó da linha do tempo — marcador + horário + aeroporto. `time`/`name` já
 *  formatados pelo chamador (pode vir vazio). */
interface FlightTimelineNode {
  time: string;
  iata: string;
  name?: string;
}

/** Uma perna da linha do tempo. Strings já formatadas/localizadas — o componente
 *  é display-only. `durationLabel` é a duração do voo desta perna e `stopLabel` é a
 *  caixa de parada (layover) APÓS esta perna (ausente na última); o chamador monta
 *  o texto (duração + eventual "troca de aeronave"). */
export interface FlightTimelineLeg {
  origin: FlightTimelineNode;
  destination: FlightTimelineNode;
  durationLabel?: string;
  /** Pontualidade histórica da perna, já formatada/localizada pelo chamador. */
  statsLabel?: string;
  /** Codeshares que revendem a perna, já formatados/localizados. */
  soldAsLabel?: string;
  stopLabel?: string;
}

/** Cabeçalho da companhia. `dirLabel` ("Ida"/"Volta") já localizado. O logo é o
 *  thumb quadrado da cia (`AirlineLogo`, servido por IATA) — o mesmo glifo de todas
 *  as superfícies de voo. */
export interface FlightDetailHeader {
  carrier?: string;
  carrierName?: string;
  flightNo?: string;
  route?: string;
  dirLabel?: string;
}

/** Um nó renderizado da coluna: o ponto + horário/aeroporto e, abaixo (menos na
 *  chegada), a duração do voo da perna e a caixa de parada. */
interface RenderNode {
  node: FlightTimelineNode;
  durationLabel?: string;
  statsLabel?: string;
  soldAsLabel?: string;
  stopLabel?: string;
  /** Último nó (chegada) = ponto cheio, sem conector nem conteúdo abaixo. */
  arrival: boolean;
}

/** Pernas → nós: um ponto por aeroporto (anel na partida/conexões, ponto cheio na
 *  chegada), sem nó de conexão duplicado. O ponto da perna i é a partida (i=0) ou a
 *  CHEGADA da perna anterior — a conexão mostra o horário de ATERRISSAGEM (é quando
 *  você chega e espera), e abaixo dele vão a duração do voo seguinte e a caixa de
 *  parada (layover) daquele trecho. */
function toNodes(legs: FlightTimelineLeg[]): RenderNode[] {
  const nodes: RenderNode[] = [];
  legs.forEach((leg, i) => {
    const previous = i === 0 ? undefined : legs[i - 1];
    const point = previous ? previous.destination : leg.origin;
    nodes.push({
      node: point,
      ...(leg.durationLabel ? { durationLabel: leg.durationLabel } : {}),
      ...(leg.statsLabel ? { statsLabel: leg.statsLabel } : {}),
      ...(leg.soldAsLabel ? { soldAsLabel: leg.soldAsLabel } : {}),
      ...(leg.stopLabel ? { stopLabel: leg.stopLabel } : {}),
      arrival: false,
    });
    if (i === legs.length - 1) nodes.push({ node: leg.destination, arrival: true });
  });
  return nodes;
}

/**
 * Vista compartilhada "Detalhe do voo": cabeçalho da companhia (logo acima + nome +
 * nº + perna + rota) e a linha do tempo por trecho — uma coluna de nós (partida /
 * conexões = anel, chegada = ponto cheio) ligados por um conector vertical, cada nó
 * com horário, aeroporto, duração do trecho e a caixa de parada (com ícone de
 * relógio). PURO e display-only — recebe strings já formatadas e localizadas pelo
 * chamador, sem fetch nem i18n próprios.
 *
 * Fonte única dos dois pontos de detalhe do voo: o drawer do web-chat
 * (`FlightDetailDrawer`, pernas reais via GET /flight/legs) e o
 * `SegmentDetailDrawer` dos cards in-chat (pernas derivadas do segmento exibido —
 * voo direto é uma perna; cada escala de `seg.connections` vira um nó sem
 * horário, com a caixa de parada quando o layover veio).
 */
export function FlightDetailView({
  header,
  legs,
}: {
  readonly header: FlightDetailHeader;
  readonly legs: FlightTimelineLeg[];
}) {
  // Uma resolução só para a guarda e para o slot: com `??` no slot, um
  // `carrierName: ''` passava a guarda (que já usava `||`) e renderizava vazio.
  const ciaName = header.carrierName?.trim() || header.carrier?.trim() || '';
  const showHeader = Boolean(ciaName || header.flightNo || header.route);
  const nodes = toNodes(legs);
  return (
    <>
      {showHeader && (
        <div className="fdt-cia">
          <span className="fdt-cia-logo">
            <AirlineLogo
              {...(header.carrier ? { iata: header.carrier } : {})}
              {...(header.carrierName ? { name: header.carrierName } : {})}
              size={DRAWER_HEADER_LOGO}
            />
          </span>
          <div className="fdt-cia-id">
            {ciaName && <div className="fdt-cia-name">{ciaName}</div>}
            <div className="fdt-cia-sub">
              {header.dirLabel && <span className="fdt-cia-dir">{header.dirLabel}</span>}
              {header.flightNo}
              {header.route && <span className="fdt-cia-route">{header.route}</span>}
            </div>
          </div>
        </div>
      )}

      <ol className="fdt-timeline">
        {nodes.map((n, i) => (
          <li
            key={`${n.node.iata}-${n.node.time}-${String(i)}`}
            className={`fdt-node${n.arrival ? ' is-arrival' : ''}`}
          >
            <span className="fdt-rail" aria-hidden="true">
              <span className="fdt-marker" />
            </span>
            <div className="fdt-content">
              <div className="fdt-time">{n.node.time}</div>
              <div className="fdt-place">
                {n.node.iata}
                {n.node.name ? ` ${n.node.name}` : ''}
              </div>
              {n.durationLabel && <div className="fdt-dur">{n.durationLabel}</div>}
              {n.statsLabel && <div className="fdt-stat">{n.statsLabel}</div>}
              {n.soldAsLabel && <div className="fdt-stat">{n.soldAsLabel}</div>}
              {n.stopLabel && (
                <div className="fdt-stop">
                  <ClockIcon size={18} aria-hidden />
                  <span>{n.stopLabel}</span>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}
